<template>
  <div id="paymentDetails" class="max-w-176 mt-12 shadow-md" data-test-id="payment_information_component">
    <SimpleReviewSummary
      v-for="(summary, summaryIndex) in paymentInformations"
      :key="summaryIndex"
      :title="summary.title"
      :icon="summary.icon"
      :multiple="true"
    >
      <template #reviewSummaryItems>
        <ReviewSummaryItem
          v-for="(item, itemIndex) in summary.items"
          :key="itemIndex"
          :item="item"
          :isLastReviewSummaryItem="!(itemIndex < summary.items.length - 1)"
          :isSelfService="true"
        >
          <template #title>
            <div v-if="item.fields.title.value" class="relative">
              <div class="text-racq-dark-grey text-base-bold" v-html="item.fields.title.value"></div>
            </div>
          </template>
          <template #content>
            <template v-if="item.name === 'NextPaymentDueDate'">
              <div class="text-base" v-html="getPaymentInfoNextDueDate(item.fields.content)"></div>
            </template>
            <template v-if="item.name === 'PaymentType'">
              <div class="text-base" v-html="getPaymentInfoPaymentType(item.fields.content)"></div>
            </template>
            <template v-if="item.name === 'PaymentDetails' && currentPolicyPaymentMethods !== null">
              <div
                class="text-base flex items-center gap-[6px]"
                v-html="getPaymentInfoPaymentDetails(item.fields.content)"
              ></div>
            </template>
            <template v-if="item.name === 'AutoRenewSettings'">
              <div class="text-base" v-html="getPaymentInfoAutoRenewSettings(item.fields.content)"></div>
            </template>
          </template>
          <template #secondaryLinks> </template>
          <template #edit>
            <template v-if="item.fields?.primaryLink?.value?.href">
              <Link
                v-if="item.fields?.primaryLink"
                :field="item.fields.primaryLink"
                :data-test-id="`link_${item.fields.primaryLink.value.title?.replaceAll(' ', '')}`"
                class="link text-base-bold"
              />
            </template>
            <template v-if="item.name === 'AutoRenewSettings'">
              <!-- Disabled if policy is in renewal, SSINSP.FR10.3 -->
              <a
                v-if="currentPolicy?.policyNumber"
                class="cursor-pointer link text-base-bold"
                data-test-id="link_AutoRenewSettingModal"
                :class="{ disabled: currentPolicy?.state?.isInRenewal }"
                @click="showAutoRenewSettingsModal()"
                >Change settings</a
              >
            </template>
          </template>
          <template #subContent>
            <template v-if="item.name === 'NextPaymentDueDate' && getPaymentInfoPaymentDueInfo">
              <div class="text-sm" v-html="getPaymentInfoPaymentDueInfo"></div>
            </template>
            <template v-if="item.name === 'PaymentType' && actionBarRuleResult.showSwitchToMonthly">
              <!-- InformationBox needed like this for the click event on the link -->
              <div class="flex w-full px-6 py-5 rounded gap-4 bg-racq-info-light-blue mt-4">
                <div>
                  <span class="w-5 h-6 flex-col justify-center items-start gap-2.5 inline-flex bg-icon-info"></span>
                </div>
                <div class="flex flex-col w-full">
                  <div class="text-racq-dark-grey text-base-bold">
                    {{ paymentTypeInfoBox?.title.value }}
                  </div>
                  <div class="text-racq-dark-grey text-m mt-[2px]">
                    {{ paymentTypeInfoBox?.content.value }}
                    <br />
                    <a class="cursor-pointer link text-base-bold" @click="switchToMonthly()">Pay monthly</a>
                  </div>
                </div>
              </div>
            </template>
            <template
              v-if="
                item.name === 'AutoRenewSettings' && currentPolicy!.state.isInRenewal && !isCurrentTermResponsivePayment
              "
            >
              <InformationBox
                :description="autoRenewInfoBox.content.value"
                :type="InformationBoxTypes.WARNING"
              ></InformationBox>
            </template>
          </template>
        </ReviewSummaryItem>
      </template>
    </SimpleReviewSummary>
  </div>
  <AutoRenewSettingsModal
    ref="autoRenewSettingsModalEl"
    :autoRenewSettingsModal="autoRenewSettingsModalDs"
    @updateAutoRenewState="handleAutoRenewUpdate"
  />
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch, nextTick } from 'vue';
import { InsurancePolicy, ISitecoreFormGenericInput } from '@/interfaces';
import { getFormInput } from '@/utils/form-input-helper';
import { SelfServiceService } from '@/services/store/self-service-service';
import ReviewSummaryItem from '@/components/ReviewSummary/ReviewSummaryItem.vue';
import SimpleReviewSummary from '@/components/ReviewSummary/SimpleReviewSummary.vue';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { format as formatDate } from 'date-fns';
import { formatCurrency } from '@/utils/text-transforms';
import { Discriminators } from '@/constants/insurance-policy-const';
import { Field, Link } from '@sitecore-jss/sitecore-jss-vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { IPaymentInstrument, PaymentMethod } from '@/interfaces/self-service/defer-payment.interface';
import { SelfServiceSessionPages } from '@/enums/self-service-session-pages';
import { useRouter } from 'vue-router';
import AutoRenewSettingsModal from './AutoRenewSettingsModal.vue';
import { useAccountStore } from '@/store';
import { CardTypes } from '@/enums/payment-types.enum';
import { toMaskedCardNumber } from '@/utils/convert';

const router = useRouter();

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  currentPolicyTemp: InsurancePolicy | undefined;
  selfServiceToggles: {
    enableSwitchMonthlyPaymentButton: boolean;
    isChangePaymentDetailsButtonEnabled: boolean;
  };
  actionBarRuleResult: any;
}>();

const accountStore = useAccountStore();

const currentPolicy = ref<InsurancePolicy>();
const paymentInformations = ref<any>();

enum FormInputs {
  paymentInformation = 'paymentInformation',
  paymentTypeInfoBox = 'paymentTypeInfoBox',
  autoRenewInfoBox = 'autoRenewInfoBox',
  annualPaymentDueLabel = 'annualPaymentDueLabel',
  monthlyPaymentDueLabel = 'monthlyPaymentDueLabel',
  autoRenewModalDialog = 'autoRenewModalDialog',
}
const paymentTypeInfoBox = ref<any>(getFormInput(props.content, FormInputs.paymentTypeInfoBox));
const autoRenewInfoBox = ref<any>(getFormInput(props.content, FormInputs.autoRenewInfoBox));
const autoRenewSettingsModalEl = ref<InstanceType<typeof AutoRenewSettingsModal>>();
const autoRenewSettingsModalDs = ref<any>();

const isCurrentTermResponsivePayment = computed(() => {
  // SSINSP.FR10.2
  return currentPolicy.value?.currentTermPaymentDetails?.discriminator === Discriminators.ResponsivePaymentDetails;
});

const isPaymentDueForCurrentTerm = computed(() => {
  const currentTermPaymentDetails = currentPolicy.value?.currentTermPaymentDetails;
  return currentTermPaymentDetails?.isPaymentDue;
});

const isPaymentDueForRenewalTerm = computed(() => {
  const renewalTermPaymentDetails = currentPolicy.value?.renewalTermPaymentDetails;
  return renewalTermPaymentDetails?.isPaymentDue;
});

const getPaymentInfoPaymentDueInfo = computed(() => {
  if (!currentPolicy.value || (!isPaymentDueForCurrentTerm.value && !isPaymentDueForRenewalTerm.value)) {
    return '';
  }

  // Payment type should always be determined from the currentTerm
  if (isCurrentTermResponsivePayment.value) {
    // Handle responsive payments (non-direct debit)
    return getFormInput(props.content, FormInputs.annualPaymentDueLabel)?.label?.value ?? '';
  } else {
    // Handle direct debit payments
    const isAnnual = currentPolicy.value?.currentTermPaymentDetails?.isAnnual ?? false;
    return (
      getFormInput(props.content, isAnnual ? FormInputs.annualPaymentDueLabel : FormInputs.monthlyPaymentDueLabel)
        ?.label?.value ?? ''
    );
  }
});

const currentPolicyPaymentMethods = ref<IPaymentInstrument | null>(null);

enum Bookmarks {
  nextPaymentDueDate = 'nextPaymentDueDate',
  paymentType = 'paymentType',
  paymentDetails = 'paymentDetails',
  deferPaymentSessionId = 'deferPaymentSessionId',
  changePaymentDetailsSessionId = 'changePaymentDetailsSessionId',
  autoRenewSettings = 'autoRenewSettings',
}

enum SummaryItemNames {
  paymentDetails = 'PaymentDetails',
  autoRenewSettings = 'AutoRenewSettings',
  nextPaymentDueDate = 'NextPaymentDueDate',
}

onMounted(async () => {
  currentPolicy.value = props.currentPolicyTemp; // hand off the prop to the reactive currentPolicy
});

watch(currentPolicy, async () => {
  // Only populate the paymentInformation reviewSumary when there's a value in currentPolicy
  if (currentPolicy.value) {
    paymentInformations.value = [
      await getPaymentInformationSummaryModel(0),
      await getPaymentInformationSummaryModel(1),
    ];
  }

  if (!isCurrentTermResponsivePayment.value) {
    // Direct debit
    if (currentPolicyPaymentMethods.value === null) {
      let currentTerm = 0;
      if (currentPolicy.value?.state.isInRenewal) {
        currentTerm = currentPolicy.value!.renewalTermPaymentDetails?.termNumber;
      } else {
        currentTerm = currentPolicy.value!.currentTermPaymentDetails?.termNumber;
      }

      currentPolicyPaymentMethods.value = await SelfServiceService.getPaymentMethods(
        currentPolicy.value!.policyNumber!,
        currentTerm
      );
    }
  }
});

async function updateQueryStringForPrimaryLink(
  policyNumber: string,
  quoteSessionPage: SelfServiceSessionPages,
  currentQueryString: string | undefined,
  bookmark: string
): Promise<string | null> {
  if (currentQueryString === undefined) {
    return null;
  }
  const response = await SelfServiceService.createQuoteSession(policyNumber, quoteSessionPage);
  if (response?.success) {
    const updatedQueryString = replaceBookmark(bookmark, currentQueryString, response.data);
    return updatedQueryString;
  }
  return null;
}

async function getPaymentInformationSummaryModel(index: number): Promise<any> {
  const tempPaymentInformationSummary = getFormInput(props.content, FormInputs.paymentInformation);
  if (!tempPaymentInformationSummary) return { title: '', icon: '', items: [] };

  let items = tempPaymentInformationSummary.fields[index].items;

  if (isCurrentTermResponsivePayment.value) {
    // Remove direct debit only reviewSummaryItems and primary links
    const valuesToRemove = [SummaryItemNames.paymentDetails, SummaryItemNames.autoRenewSettings];
    items = items.filter((x: any) => !valuesToRemove.includes(x.name));
  }

  // Handle 'Defer payment' link
  const deferPaymentItemIndex = items.findIndex((x: any) => x.name === SummaryItemNames.nextPaymentDueDate);
  // Always looking at current term for defer payment link visibility
  const showDeferPaymentLink =
    !isCurrentTermResponsivePayment.value &&
    isPaymentDueForCurrentTerm.value &&
    !currentPolicy.value!.currentTermPaymentDetails.isAnnual;

  if (deferPaymentItemIndex !== -1) {
    if (!showDeferPaymentLink) {
      // Need to hide the 'Defer payment' link, so set it to undefined
      items[deferPaymentItemIndex].fields.primaryLink.value = undefined;
    } else {
      // Update 'Defer payment' primary link with a quoteSessionId
      const updatedPrimaryLinkQueryString = await updateQueryStringForPrimaryLink(
        currentPolicy.value!.policyNumber!,
        SelfServiceSessionPages.DeferPaymentPage,
        items[deferPaymentItemIndex].fields.primaryLink.value.querystring,
        Bookmarks.deferPaymentSessionId
      );

      items[deferPaymentItemIndex].fields.primaryLink.value.querystring = updatedPrimaryLinkQueryString;
    }
  }

  // Handle 'Change payment details' link
  const changePaymentDetailsItemIndex = items.findIndex((x: any) => x.name === SummaryItemNames.paymentDetails);
  const showChangePaymentDetailsLink =
    !isCurrentTermResponsivePayment.value && props.selfServiceToggles.isChangePaymentDetailsButtonEnabled;

  if (changePaymentDetailsItemIndex !== -1) {
    if (!showChangePaymentDetailsLink) {
      // Need to hide the 'Change payment details' link, so set it to undefined
      items[changePaymentDetailsItemIndex].fields.primaryLink.value = undefined;
    } else {
      // Update 'Change payment details' primary link with a quoteSessionId
      const updatedPrimaryLinkQueryString = await updateQueryStringForPrimaryLink(
        currentPolicy.value!.policyNumber!,
        SelfServiceSessionPages.ChangePaymentDetailsPage,
        items[changePaymentDetailsItemIndex].fields.primaryLink.value.querystring,
        Bookmarks.changePaymentDetailsSessionId
      );

      items[changePaymentDetailsItemIndex].fields.primaryLink.value.querystring = updatedPrimaryLinkQueryString;
    }
  }

  return {
    title: tempPaymentInformationSummary.fields[index].summaryTitle,
    icon: tempPaymentInformationSummary.fields[index].icon,
    items: items,
  };
}

async function switchToMonthly() {
  const response = await SelfServiceService.createQuoteSession(
    currentPolicy.value!.policyNumber,
    SelfServiceSessionPages.ChangeMonthlyPaymentPage
  );
  if (response?.success) {
    const newPath = '/My-RACQ/Insurance/Switch-to-monthly';
    const newQuery = { quoteSessionId: response.data };
    router.push({
      path: newPath,
      query: newQuery,
    });
  }
}

async function showAutoRenewSettingsModal() {
  const autoRenewModalDialogDs = getFormInput(props.content, FormInputs.autoRenewModalDialog) ?? {};
  const isAutoRenewEnabled = currentPolicy.value?.currentRenewalPreference ?? false;

  autoRenewSettingsModalDs.value = {
    ...autoRenewModalDialogDs,
    currentPolicy: currentPolicy.value,
    currentRenewalPreference: isAutoRenewEnabled ?? false,
  };
  await nextTick();
  autoRenewSettingsModalEl?.value.showModal();
}

async function handleAutoRenewUpdate(newState: boolean) {
  currentPolicy.value = accountStore.identityDetails?.portfolio?.insurancePolicies?.find(
    (x) => x.policyNumber === props.currentPolicyTemp?.policyNumber
  );

  //Force refresh of review summary
  paymentInformations.value = [await getPaymentInformationSummaryModel(0), await getPaymentInformationSummaryModel(1)];
  await nextTick();
}

function getPaymentInfoAutoRenewSettings(content: Field<string>): string {
  if (
    currentPolicy.value?.currentRenewalPreference === null ||
    currentPolicy.value?.currentRenewalPreference === undefined
  ) {
    return '';
  }
  const isAutoRenewEnabled = currentPolicy.value?.currentRenewalPreference ?? false;
  const iconClass = isAutoRenewEnabled ? 'bg-icon-success' : 'bg-icon-danger-dark-grey';
  const statusText = isAutoRenewEnabled ? 'Auto renew is on' : 'Auto renew is off';
  const icon = `<span class="${iconClass} icon w-4 h-4 bg-no-repeat bg-contain inline-block mr-2"></span>`;

  return replaceBookmark(Bookmarks.autoRenewSettings, content.value, `${icon}${statusText}`.replaceAll('"', "'"));
}

function getPaymentInfoPaymentDetails(content: Field<string>): string {
  const bankIcon = `<span class="bg-icon-bank-account icon w-3 h-3 bg-no-repeat bg-contain inline-block"></span>`;
  const mastercardIcon = `<span class="bg-icon-mastercard icon w-6 h-5 bg-no-repeat bg-contain inline-block align-middle"></span>`;
  const visaIcon = `<span class="bg-icon-visa icon w-3 h-3 bg-no-repeat bg-contain inline-block align-middle"></span>`;

  let paymentDetailsText = '';

  if (currentPolicyPaymentMethods.value?.paymentMethod === PaymentMethod.BankAccount) {
    // Bank direct debit
    paymentDetailsText = `${bankIcon}Bank account ${currentPolicyPaymentMethods.value.bankAccountDetails.bankStateBranchNumber} - ${currentPolicyPaymentMethods.value.bankAccountDetails.accountNumber}`;
  } else {
    // Card direct debit
    const cardType = currentPolicyPaymentMethods.value?.creditCardDetails.cardType;
    let cardIcon = '';
    if (cardType?.toLowerCase() === CardTypes.MasterCard) {
      cardIcon = mastercardIcon;
    } else if (cardType?.toLowerCase() === CardTypes.Visa) {
      cardIcon = visaIcon;
    } else {
      // Edge case
      cardIcon = bankIcon;
    }
    paymentDetailsText = `${cardIcon}${cardType} ${toMaskedCardNumber(currentPolicyPaymentMethods.value?.creditCardDetails.maskedCardNumber)} - ${currentPolicyPaymentMethods.value?.creditCardDetails.cardExpiry}`;
  }

  return replaceBookmark(Bookmarks.paymentDetails, content.value, paymentDetailsText.replaceAll('"', "'"));
}

const PAYMENT_TYPES = {
  ANNUAL_INVOICE: 'Annual invoice',
  MONTHLY_DIRECT_DEBIT: 'Monthly direct debit',
  ANNUAL_DIRECT_DEBIT: 'Annual direct debit',
} as const;

function getPaymentInfoPaymentType(content: Field<string>): string {
  if (!content?.value || !currentPolicy.value) {
    return '';
  }

  try {
    let paymentType: string;
    let isAnnual: boolean | undefined;

    // Payment type should always be determined from the currentTerm
    if (isCurrentTermResponsivePayment.value) {
      // non direct-debit
      paymentType = PAYMENT_TYPES.ANNUAL_INVOICE;
    } else {
      // direct debit
      isAnnual = currentPolicy.value.currentTermPaymentDetails.isAnnual;
      paymentType = isAnnual ? PAYMENT_TYPES.ANNUAL_DIRECT_DEBIT : PAYMENT_TYPES.MONTHLY_DIRECT_DEBIT;
    }

    return replaceBookmark(Bookmarks.paymentType, content.value, paymentType).replaceAll('"', "'");
  } catch (error) {
    console.error('Error in replaceBookmark:', error);
    return '';
  }
}

// Formats the duedate text for the Next Payment Due item
function formatDueDateText(dueDate: Date | undefined, amount: number | undefined, calendarIcon: string) {
  if (dueDate === undefined || amount === undefined) {
    return `${calendarIcon}No payments scheduled`;
  }
  return `${calendarIcon}${formatDate(dueDate, 'dd MMMM yyyy')} for ${formatCurrency(amount, 2)}*`;
}

function getPaymentInfoNextDueDate(content: Field<string>): string {
  const calendarIcon = `<span class="bg-calendar-filled-icon icon w-3 h-3 bg-no-repeat bg-contain inline-block mr-2"></span>`;
  let nextDueDateText = `${calendarIcon}No payments scheduled`;

  if (!currentPolicy.value) {
    return replaceBookmark(Bookmarks.nextPaymentDueDate, content.value, nextDueDateText.replaceAll('"', "'"));
  }

  const { currentTermPaymentDetails, renewalTermPaymentDetails } = currentPolicy.value;

  if (isPaymentDueForCurrentTerm.value && currentTermPaymentDetails) {
    // Current term payment due
    nextDueDateText = formatDueDateText(
      currentTermPaymentDetails.nextInvoiceDueDate,
      currentTermPaymentDetails.nextInvoiceAmountDue,
      calendarIcon
    );
  } else if (isPaymentDueForRenewalTerm.value && renewalTermPaymentDetails) {
    // Renewal term payment due
    nextDueDateText = formatDueDateText(
      renewalTermPaymentDetails.nextInvoiceDueDate,
      renewalTermPaymentDetails.nextInvoiceAmountDue,
      calendarIcon
    );
  }

  return replaceBookmark(Bookmarks.nextPaymentDueDate, content.value, nextDueDateText.replaceAll('"', "'"));
}
</script>
